import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import TrendIndicator from './TrendIndicator';

/**
 * SalesBarChart - Reusable bar chart for sales analytics
 * @param {Array} data - Array of items with sales data
 * @param {string} type - "best" or "slow" (for trend interpretation)
 * @param {string} metricUnit - Unit of measurement (e.g., 'items', 'lbs', 'jars')
 */
const SalesBarChart = ({ data, type = 'best', metricUnit = 'items' }) => {
    const [isDarkMode, setIsDarkMode] = useState(false);

    useEffect(() => {
        // Check if dark mode is active
        const checkDarkMode = () => {
            const htmlElement = document.documentElement;
            const theme = htmlElement.getAttribute('data-bs-theme');
            setIsDarkMode(theme === 'dark');
        };

        checkDarkMode();

        // Listen for theme changes
        const observer = new MutationObserver(checkDarkMode);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-bs-theme'] });

        return () => {
            observer.disconnect();
        };
    }, []);

    // Color palette for bars (same as pie chart)
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B9D', '#8DD1E1', '#D084D0'];

    // Format data for Recharts
    const chartData = data.map((item, index) => {
        // Keep actual value for display
        const actualValue = item.total_issued;
        // Use a minimum value for chart rendering (0.1% of max value, or 1 if all zeros)
        const maxValue = Math.max(...data.map(d => d.total_issued));
        const minRenderValue = maxValue > 0 ? maxValue * 0.001 : 1;
        const renderValue = actualValue > 0 ? actualValue : minRenderValue;

        // Manufacturer information
        const manufacturer = item.manufacturer || '';
        const shortManufacturer = manufacturer.length > 15 ? manufacturer.substring(0, 15) + '...' : manufacturer;

        // Create display name with manufacturer for X-axis
        const productName = item.description || item.sku || item.gtin;
        const displayName = manufacturer ? `${productName}\n(${shortManufacturer})` : productName;
        const shortDisplayName = displayName.length > 60
            ? `${displayName.substring(0, 60)}...`
            : displayName;

        return {
            name: productName,
            manufacturer: manufacturer,
            shortManufacturer: shortManufacturer,
            shortName: shortDisplayName,
            value: renderValue,  // Used for bar chart rendering
            actualValue: actualValue,  // Used for labels and tooltips
            trend: item.trend,
            trend_percentage: item.trend_percentage,
            color: COLORS[index % COLORS.length],
            fullItem: item,
        };
    });

    // Format number with thousand separators
    const formatNumber = (value) => {
        if (metricUnit === '$') {
            // Format as currency
            return '$' + value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
        if (metricUnit === 'lbs') {
            return value.toFixed(1).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
        return Math.round(value).toLocaleString('en-US');
    };

    // Custom tooltip
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            const displayValue = formatNumber(data.actualValue);
            return (
                <div
                    style={{
                        background: '#ffffff',
                        padding: '12px',
                        border: '2px solid #333',
                        borderRadius: '6px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                        color: '#333',
                    }}
                >
                    <p style={{ margin: 0, fontWeight: 'bold', marginBottom: '6px', color: '#000', fontSize: '13px' }}>
                        {data.name}
                    </p>
                    {data.manufacturer && (
                        <p style={{ margin: 0, color: '#666', fontSize: '11px', marginBottom: '4px', fontStyle: 'italic' }}>
                            Manufacturer: {data.manufacturer}
                        </p>
                    )}
                    <p style={{ margin: 0, color: '#333', fontSize: '12px' }}>
                        {metricUnit === 'lbs' ? 'Weight' : metricUnit === '$' ? 'Profit' : 'Quantity'}: <strong style={{ color: '#000' }}>{metricUnit === '$' ? displayValue : `${displayValue} ${metricUnit}`}</strong>
                    </p>
                    <div style={{ marginTop: '6px' }}>
                        <TrendIndicator
                            trend={data.trend}
                            percentage={data.trend_percentage}
                            type={type}
                        />
                    </div>
                </div>
            );
        }
        return null;
    };

    // Custom label to show value and trend on bars
    const renderCustomLabel = (props) => {
        const { x, y, width, height, value, index } = props;
        const item = chartData[index];
        const displayValue = formatNumber(item.actualValue);

        // Trend arrow
        const trendIcon = item.trend === 'up' ? '↑' : item.trend === 'down' ? '↓' : '→';
        const trendColor = type === 'best'
            ? (item.trend === 'up' ? '#28a745' : item.trend === 'down' ? '#dc3545' : '#888')
            : (item.trend === 'down' ? '#28a745' : item.trend === 'up' ? '#dc3545' : '#888');

        return (
            <g>
                {/* Value label on top of bar - with background for visibility */}
                <rect
                    x={x + width / 2 - 25}
                    y={y - 22}
                    width="50"
                    height="14"
                    fill="rgba(255, 255, 255, 0.9)"
                    stroke="#333"
                    strokeWidth="0.5"
                    rx="3"
                />
                <text
                    x={x + width / 2}
                    y={y - 11}
                    fill="#000"
                    textAnchor="middle"
                    fontSize="11"
                    fontWeight="bold"
                >
                    {displayValue}
                </text>
                {/* Trend indicator - with background circle */}
                <circle
                    cx={x + width / 2}
                    cy={y - 31}
                    r="9"
                    fill="rgba(255, 255, 255, 0.9)"
                    stroke="#333"
                    strokeWidth="0.5"
                />
                <text
                    x={x + width / 2}
                    y={y - 27}
                    fill={trendColor}
                    textAnchor="middle"
                    fontSize="14"
                    fontWeight="bold"
                >
                    {trendIcon}
                </text>
            </g>
        );
    };

    return (
        <ResponsiveContainer width="100%" height={450}>
            <BarChart data={chartData} margin={{ top: 50, right: 30, left: 20, bottom: 120 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#444' : '#ccc'} />
                <XAxis
                    dataKey="shortName"
                    angle={-45}
                    textAnchor="end"
                    height={120}
                    interval={0}
                    tick={{ fill: isDarkMode ? '#ffffff' : '#000000', fontSize: '12px', fontWeight: 'bold' }}
                    stroke={isDarkMode ? '#666' : '#999'}
                />
                <YAxis
                    tick={{ fill: isDarkMode ? '#ffffff' : '#000000', fontSize: '12px', fontWeight: 'bold' }}
                    stroke={isDarkMode ? '#666' : '#999'}
                    tickFormatter={(value) => {
                        if (metricUnit === '$') {
                            return '$' + Math.round(value).toLocaleString('en-US');
                        }
                        if (metricUnit === 'lbs') {
                            return value.toFixed(0);
                        }
                        return Math.round(value).toLocaleString('en-US');
                    }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" label={renderCustomLabel}>
                    {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

export default SalesBarChart;
